import { useEffect, useRef, useState } from 'react'
import { Heading } from 'ui-kit/typography'
import { LINKS } from 'routes/constants'
import { Button } from 'ui-kit/actions/button'
import { ScenariosCard } from 'routes/components/Features/ScenarioCard'
import { getReadableScenariosTagName } from 'routes/Scenarios/helpers'
import {
  ScenarioTemplateCompact,
  SimilarScenariosData,
} from 'routes/Scenarios/helpers/types'
import clsx from 'clsx'
import classes from './SimilarScenarios.module.scss'

const INITIAL_SCENARIOS_COUNT = 4

export function SimilarScenarios({
  similarScenarios,
}: {
  similarScenarios: SimilarScenariosData[]
}) {
  if (similarScenarios.length === 0) {
    return null
  }

  return (
    <section className={classes.section}>
      <Heading as={'h2'} size={'md'}>
        Explore Similar Templates
      </Heading>
      {similarScenarios.map(({ title, items }) => (
        <SimilarScenariosBlock key={title} title={title} scenarios={items} />
      ))}
    </section>
  )
}

export function SimilarScenariosBlock({
  title,
  scenarios,
}: {
  title: string
  scenarios: ScenarioTemplateCompact[]
}) {
  if (scenarios.length === 0) {
    return null
  }

  const [loadMore, setLoadMore] = useState(false)
  const elementToScroll = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoadMore(false)
  }, [scenarios])

  const toggleLoadMoreBtn = () => {
    if (loadMore) {
      window.scrollTo({
        top: elementToScroll.current?.offsetTop,
        left: 0,
        behavior: 'smooth',
      })
    }
    setLoadMore(!loadMore)
  }

  const isMoreScenariosAvailable = scenarios.length > INITIAL_SCENARIOS_COUNT

  const numberOfAdditionalScenarios = scenarios.length - INITIAL_SCENARIOS_COUNT

  const visibleScenarios = loadMore
    ? scenarios
    : scenarios.slice(0, INITIAL_SCENARIOS_COUNT)

  const scenariosTag = getReadableScenariosTagName(title)

  const showWrapper = visibleScenarios.length > 1

  return (
    <div className={classes.wrapper} ref={elementToScroll}>
      <ul
        className={clsx(
          classes.list,
          showWrapper && classes.list__bordered,
          !showWrapper && classes.list__single,
        )}
      >
        {visibleScenarios.map((scenario) => (
          <SimilarScenariosBlockCard key={scenario.key} scenario={scenario} />
        ))}
      </ul>
      {isMoreScenariosAvailable && (
        <Button
          category='secondary'
          onClick={toggleLoadMoreBtn}
          className={classes.button}
        >
          {loadMore
            ? 'Hide additional scenarios'
            : `... and ${numberOfAdditionalScenarios} more from ${scenariosTag}`}
        </Button>
      )}
    </div>
  )
}

const SimilarScenariosBlockCard = ({
  scenario,
}: {
  scenario: ScenarioTemplateCompact
}) => {
  const scenarioPageLink = `${LINKS.SCENARIOS}/${scenario.key}`

  return (
    <li className={classes.item}>
      <ScenariosCard
        scenario={scenario}
        href={scenarioPageLink}
        isVertical
        className={classes.card}
      />
    </li>
  )
}
